import { inject, Injectable } from '@angular/core';
import { MatrixService } from '../matrix/matrix.service';
import { IContent, MatrixError, UploadResponse } from 'matrix-js-sdk';
import { EncryptedFile, MediaEventContent, MediaEventInfo } from 'matrix-js-sdk/lib/@types/media';
import encrypt, { IEncryptedFile } from 'matrix-encrypt-attachment';
import { RoomListAccessService } from '../room-list/room-list-access.service';
import { encode } from 'blurhash';
import { matrixErrorAsJson } from '../../errors/error-with-status';
import { AppConfigService } from '../config/app-config.service';
import { MsgType } from '../../components/sb-client/sb-model/display-message';

@Injectable({
  providedIn: 'root'
})
/**
 * Der FileService stellt das An- und Abmelden bereit.
 * Implementation of a matrix attachment file helper with encryption/decryption.
 * Umgesetze AFOs:
 * A_25363 - Testtreiber-Modul
 * A_25361 - keine Modifikation der Inhalte
 */
export class FileService {
  /**
   * This mediaStorage used for intermediate file up- and downloads through the testDriver mediaService api.
   * It is accessed by the testDriver messageService implementation to create encrypted/unencrypted message attachments,
   * based on these unencrypted blobs, referenced by their fileId.
   * Also, every attachment of every message returned to the caller by the testDriver is stored here so that the caller
   * can download the file via the media service with an id.
   * This storage is only needed to serve the testDriver api and is not used during normal client operations.
   */
  public mediaStorage = new Map<string, Blob>();
  private readonly configService = inject(AppConfigService);
  private readonly VALID_WHITELIST_STATES = ['disabled', 'enabled'];

  private readonly matrixService = inject(MatrixService);
  private readonly roomListAccessService = inject(RoomListAccessService);
  private config?: number;

  public async getConfig(): Promise<number | undefined> {
    this.config = this.config ?? (await this.matrixService.client.getMediaConfig())['m.upload.size'];
    return this.config;
  }

  public async getMaxSize(): Promise<number> {
    const config = await this.getConfig();
    return config ?? 0;
  }

  public async uploadedUnencryptedFile(blob: Blob): Promise<{ url: string; file?: EncryptedFile }> {
    const { content_uri: url } = await this.uploadContent(blob);
    return { url };
  }

  public async uploadedEncryptedFile(blob: Blob): Promise<{ url: string; file: EncryptedFile }> {
    const data = await blobToArrayBuffer(blob);
    const encryptResult = await encrypt.encryptAttachment(data);
    const { content_uri: url } = await this.uploadEncryptedFileToServer(encryptResult);
    const file = encryptedFileMetadata(url, encryptResult);
    return { url, file };
  }

  public async checkUploadSize(file: File): Promise<boolean> {
    let MAX_UPLOAD_SIZE = 0;

    try {
      const config = await this.getConfig();
      if (config) {
        MAX_UPLOAD_SIZE = config;
      }

      return file.size <= MAX_UPLOAD_SIZE;
    } catch (error) {
      console.error('getMediaConfig fehlgeschlagen', error);

      if (!(error instanceof MatrixError)) {
        throw error;
      }
      throw matrixErrorAsJson('GetMediaConfigError', error);
    }
  }

  public uploadEncryptedFileToServer(encryptResult: EncryptResult): Promise<{ content_uri: string }> {
    const blob = new Blob([encryptResult.data]);
    return this.matrixService.client.uploadContent(blob, {
      includeFilename: false,
      type: 'application/octet-stream'
    });
  }

  /**
   * Downloads a media file from a given Matrix content (MXC) URL.
   * This method is used to retrieve the raw encrypted file from the Matrix media server.
   * @param mxcUrl The Matrix content URL (mxc://...) of the media file
   * @returns A Blob containing the downloaded media file
   * @throws Error if the MXC URL is invalid or the download fails
   */
  public async downloadSource(mxcUrl: string): Promise<Blob> {
    const url = this.matrixService.client.mxcUrlToHttp(mxcUrl);
    if (!url) {
      throw new Error('Invalid MXC URL when executing downloadSource');
    }

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to download media: ${response.statusText}`);
    }
    return response.blob();
  }

  /**
   * Decrypts an encrypted Matrix content file.
   * This method uses the matrix-encrypt-attachment library to decrypt an encrypted media file.
   * The MIME type is determined from the event info and applied to the resulting Blob.
   * @param content The Matrix content containing the encrypted file metadata and file URL
   * @returns A Blob containing the decrypted file or an empty Blob if decryption fails
   */
  public async decryptFile(content: IContent): Promise<Blob> {
    try {
      const info: MediaEventInfo = content['info'];
      const file: Partial<MediaEventContent> = content['file'];
      const encryptedFile: IEncryptedFile = content['file'];

      if (!file?.url) {
        console.warn('FileURL not found');
        return new Blob();
      }

      const response = await this.downloadSource(file.url);
      const arrayBuffer = await response.arrayBuffer();

      const decryptedArrayBuffer = await encrypt.decryptAttachment(arrayBuffer, encryptedFile);

      let mimetype = info?.mimetype ? info.mimetype.split(';')[0].trim() : '';
      mimetype = getBlobMimeType(mimetype);

      return new Blob([decryptedArrayBuffer], { type: mimetype });
    } catch (error) {
      console.error('\n' + 'Error decrypting file:', error);
      return new Blob();
    }
  }

  public isAllowedMimeType(mimetype: string): boolean {
    if (this.useWhiteList()) {
      return mimetype in ALLOWED_FILE_TYPES;
    } else {
      return true;
    }
  }

  public async validateFileType(file: File): Promise<boolean> {
    if (!this.useWhiteList()) {
      return true;
    }
    const fileName = file.name.toLowerCase();
    const extension = fileName.substring(fileName.lastIndexOf('.') + 1);

    if (!ALLOWED_FILE_EXTENSIONS.has(extension)) {
      return false;
    }

    if (!(file.type in ALLOWED_FILE_TYPES)) {
      return false;
    }

    const buffer = await file.slice(0, 32).arrayBuffer();
    const fileBytes = new Uint8Array(buffer);
    return this.checkFileSignature(fileBytes, file.type);
  }

  public checkFileSignature(fileBytes: Uint8Array, mimetype: string): boolean {
    if (!this.useWhiteList()) {
      return true;
    }
    const allowedSignatures = ALLOWED_FILE_TYPES[mimetype];
    if (!allowedSignatures) {
      return false;
    }
    if (allowedSignatures.length === 0) {
      const allKnownSignatures = Object.values(ALLOWED_FILE_TYPES)
        .flat()
        .filter(sig => sig.length > 0);
      return !this.checkFileSignatureInt(fileBytes, allKnownSignatures);
    }
    return this.checkFileSignatureInt(fileBytes, allowedSignatures);
  }

  async uploadFileForRoom(
    roomId: string,
    blob: Blob,
    isEncrypted?: boolean
  ): Promise<{ url: string; file?: EncryptedFile }> {
    // Der Aufrufer kann die Verschlüsselung explizit vorgeben (gemeinsamer Sende-Pfad
    // von App und Testtreiber). Fehlt die Angabe, wird sie aus den Rauminfos abgeleitet.
    const encrypted = isEncrypted ?? this.roomListAccessService.getRoomInfo(roomId)?.apiRoom?.isEncrypted ?? false;

    return encrypted ? this.uploadedEncryptedFile(blob) : this.uploadedUnencryptedFile(blob);
  }

  async prepareMediaEventContent(
    roomId: string,
    file: File,
    content: MediaEventContent,
    isEncrypted?: boolean
  ): Promise<MediaEventContent> {
    const uploadResult = await this.uploadFileForRoom(roomId, file, isEncrypted);

    if (uploadResult.file) {
      content.file = uploadResult.file;
    } else {
      content.url = uploadResult.url;
    }

    return content;
  }

  async getImageInfo(file: File): Promise<ImageInfoWithPreview> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = (): void => {
        URL.revokeObjectURL(objectUrl);

        const canvas = document.createElement('canvas');
        const width = img.width;
        const height = img.height;

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context could not be created'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        const imageData = ctx.getImageData(0, 0, width, height);

        const blurhash = encode(imageData.data, width, height, 4, 3);

        resolve({
          w: width,
          h: height,
          thumbnail_info: {
            w: width,
            h: height,
            mimetype: file.type,
            size: file.size
          },
          'tim_messenger.blurhash': blurhash
        });
      };

      img.onerror = (): void => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Image preview could not be loaded'));
      };

      img.src = objectUrl;
    });
  }

  /**
   * Baut den Media-Event-Content für eine Datei zusammen und ergänzt dabei nur die
   * Felder, die im übergebenen preset noch fehlen ("respect caller, fill only missing").
   * Dadurch können App-UI (preset leer) und Testtreiber (preset aus dem gelieferten
   * MessageContent) denselben Assembly-Code nutzen, ohne dass vom Aufrufer gelieferte
   * Werte (msgtype/body/info) überschrieben werden.
   */
  public async mergeMediaEventContent(
    file: File,
    preset?: Partial<ExtendedMediaEventContent>
  ): Promise<ExtendedMediaEventContent> {
    const msgtype = preset?.msgtype ?? (file.type.startsWith('image/') ? MsgType.Image : MsgType.File);

    const content: ExtendedMediaEventContent = {
      ...preset,
      body: preset?.body ?? file.name,
      filename: preset?.filename ?? file.name,
      msgtype,
      info: {
        size: file.size,
        mimetype: file.type || 'application/octet-stream',
        ...preset?.info
      }
    };

    // Bildinfos (Breite/Höhe/Thumbnail/blurhash) nur berechnen, wenn der Aufrufer sie
    // nicht bereits geliefert hat. Die aufwändige Bildanalyse entfällt dann.
    const currentInfo = content.info as Partial<BasicImageInfo> | undefined;
    const needsImageInfo = msgtype === MsgType.Image && (currentInfo?.w === undefined || currentInfo?.h === undefined);

    if (needsImageInfo) {
      const imageInfo = await this.getImageInfo(file);

      content.info = {
        ...imageInfo,
        ...content.info
      };
    }

    return content;
  }

  private useWhiteList(): boolean {
    const value = this.configService.get('useWhitelist');
    if (value === '') {
      return true;
    }
    if (!this.VALID_WHITELIST_STATES.includes(value)) {
      throw new Error('Invalid configuration of Whitelist');
    }
    return value === 'enabled';
  }

  private uploadContent(blob: Blob): Promise<UploadResponse> {
    return this.matrixService.client.uploadContent(blob);
  }

  private checkFileSignatureInt(fileBytes: Uint8Array, signatures: number[][]): boolean {
    return signatures.some(signature => {
      if (fileBytes.length < signature.length) {
        return false;
      }
      return signature.every((byte, j) => fileBytes[j] === byte);
    });
  }
}

export interface EncryptResult {
  data: ArrayBuffer;
  info: encrypt.IEncryptedFile;
}

export interface BasicImageInfo {
  w: number;
  h: number;
}

export function encryptedFileMetadata(url: string, encryptResult: EncryptResult): EncryptedFile {
  return {
    url,
    key: encryptResult.info.key,
    iv: encryptResult.info.iv,
    hashes: encryptResult.info.hashes,
    v: encryptResult.info.v
  } as EncryptedFile;
}

export function blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  if (blob.arrayBuffer) {
    return blob.arrayBuffer();
  } else {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (): void => resolve(reader.result as ArrayBuffer);
      reader.onerror = reject;
      reader.readAsArrayBuffer(blob);
    });
  }
}

export const ALLOWED_FILE_EXTENSIONS: ReadonlySet<string> = new Set([
  'apng',
  'gif',
  'jpeg',
  'jpg',
  'png',
  'heic',
  'tiff',
  'txt',
  'rtf',
  'csv',
  'pdf',
  'docx'
]);

export const ALLOWED_FILE_TYPES: Record<string, number[][]> = {
  'image/apng': [[0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]],
  'image/gif': [[0x47, 0x49, 0x46, 0x38]],
  'image/jpeg': [[0xff, 0xd8, 0xff]],
  'image/jpg': [[0xff, 0xd8, 0xff]],
  'image/png': [[0x89, 0x50, 0x4e, 0x47]],
  'image/heic': [[0x00, 0x00, 0x00, 0x18, 0x66, 0x74, 0x79, 0x70, 0x68, 0x65, 0x69, 0x63]],
  'image/tiff': [
    [0x49, 0x49, 0x2a, 0x00],
    [0x4d, 0x4d, 0x00, 0x2a]
  ],
  'text/plain': [],
  'text/rtf': [[0x7b, 0x5c, 0x72, 0x74, 0x66]],
  'text/csv': [],
  'application/pdf': [[0x25, 0x50, 0x44, 0x46]],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [[0x50, 0x4b, 0x03, 0x04]]
};

/**
 * Wandelt eine Matrix content_uri (mxc://server/mediaId) in einen WAF-sicheren,
 * opaken fileId um, der weder '/' noch ':' enthält. Server und mediaId bleiben
 * erhalten, sodass der Schlüssel kollisionsfrei und deterministisch ist.
 *
 * Wird sowohl beim Upload (eigene Medien) als auch beim Empfang föderierter
 * Medien verwendet, damit derselbe mxc-Verweis stets denselben mediaStorage-Key ergibt.
 */
export function toSafeFileId(contentUri: string): string {
  return contentUri.replace(/^mxc:\/\//, '').replaceAll('/', '_');
}

export function getBlobMimeType(mimetype: string): string {
  if (!Object.keys(ALLOWED_FILE_TYPES).includes(mimetype)) {
    return 'application/octet-stream';
  }
  return mimetype;
}

export function downloadFile(blob: Blob, fileName: string): void {
  try {
    const blobUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      link.remove();
      URL.revokeObjectURL(blobUrl);
    }, 100);
  } catch (error) {
    console.error('Error downloading file:', error);
    throw error;
  }
}

export interface ImageInfoWithPreview {
  w: number;
  h: number;
  thumbnail_info: {
    w: number;
    h: number;
    mimetype: string;
    size: number;
  };
  'tim_messenger.blurhash'?: string;
}
export type ExtendedMediaEventContent = MediaEventContent & {
  'org.matrix.msc1767.text'?: string;
  format?: string;
  formatted_body?: string;
  'm.mentions'?: {
    user_ids: string[];
    room: boolean;
  };
};
